import { useEffect, useState } from 'react'
import { db } from '../firebase'
import { collection, getDocs } from 'firebase/firestore'

export default function Productos() {
  const [productos, setProductos] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const isReactSnap = typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap'

  useEffect(() => {
    if (isReactSnap) {
      setProductos([])
      setLoading(false)
      return
    }

    const fetchProductos = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'productos'))
        const allData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
        setProductos(allData.filter(item => item.categoria === 'producto' || !item.categoria))
        setLoading(false)
      } catch (err) {
        setError('Error al cargar los productos')
        setLoading(false)
      }
    }
    fetchProductos()
  }, [isReactSnap])

  const handleWhatsApp = (producto) => {
    const precioNum = parseFloat(producto.precio)
    const precioTexto = producto.ocultarprecio || isNaN(precioNum) ? 'Consultar' : `S/ ${precioNum.toFixed(2)}`
    const mensaje = `Hola, estoy interesado en: *${producto.nombre}*\n\n💰 Precio: ${precioTexto}\n📝 ${producto.descripcion}\n\n¿Puedo obtener más información?`
    window.open(`https://wa.me/+51933534235?text=${encodeURIComponent(mensaje)}`, '_blank')
  }

  if (loading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-5">
        {[...Array(10)].map((_, i) => (
          <div key={i} className="rounded-2xl overflow-hidden animate-pulse">
            <div className="aspect-square bg-gray-100" />
            <div className="p-4 space-y-2">
              <div className="h-3 bg-gray-100 rounded w-3/4" />
              <div className="h-3 bg-gray-100 rounded w-1/2" />
            </div>
          </div>
        ))}
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex justify-center py-20">
        <p className="text-sm text-gray-400">{error}</p>
      </div>
    )
  }

  if (productos.length === 0) {
    return (
      <div className="flex justify-center py-12">
        <p className="text-sm text-gray-400">Aún no hay productos publicados.</p>
      </div>
    )
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-5">
      {productos.map((producto) => {
        const precioNum = parseFloat(producto.precio)
        const anteriorNum = producto.precioanterior ? parseFloat(producto.precioanterior) : null
        const oculto = !!producto.ocultarprecio
        const descuento = !oculto && anteriorNum && !isNaN(anteriorNum) && !isNaN(precioNum) && anteriorNum > precioNum
          ? Math.round(((anteriorNum - precioNum) / anteriorNum) * 100) : null

        return (
          <div
            key={producto.id}
            className="group bg-white rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_12px_40px_-12px_rgba(54,109,115,0.2)]"
            onClick={() => handleWhatsApp(producto)}
          >
            {/* Imagen */}
            <div className="relative aspect-square overflow-hidden bg-[#f5f5f7]">
              {producto.imagen ? (
                <img
                  src={producto.imagen}
                  alt={producto.nombre}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <svg className="w-10 h-10 text-gray-200" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                </div>
              )}
              {!oculto && descuento && (
                <span className="absolute top-2.5 left-2.5 bg-[#366D73] text-white text-[10px] font-bold px-2 py-0.5 rounded-md">
                  -{descuento}%
                </span>
              )}
            </div>

            {/* Info */}
            <div className="p-4">
              <h3 className="text-[13px] font-semibold text-[#1d1d1f] leading-snug line-clamp-2 mb-2 min-h-[2.5em]">
                {producto.nombre}
              </h3>

              {oculto ? (
                <p className="text-sm font-semibold text-[#366D73]">Consultar precio</p>
              ) : (
                <div className="flex items-baseline gap-1.5 flex-wrap">
                  {!isNaN(precioNum) && (
                    <span className="text-[15px] font-bold text-[#1d1d1f]">S/ {precioNum.toFixed(2)}</span>
                  )}
                  {anteriorNum && !isNaN(anteriorNum) && (
                    <span className="text-[11px] text-gray-400 line-through">S/ {anteriorNum.toFixed(2)}</span>
                  )}
                </div>
              )}

              <p className="text-[11px] text-gray-400 leading-relaxed line-clamp-2 mt-1.5">
                {producto.descripcion}
              </p>

              <button className="mt-3 w-full py-2 px-3 rounded-lg bg-[#366D73] hover:bg-[#3B8C8C] text-white text-[11px] font-semibold transition-colors duration-200 flex items-center justify-center gap-1.5">
                <svg className="w-3 h-3 shrink-0" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Consultar
              </button>
            </div>
          </div>
        )
      })}
    </div>
  )
}
